/* ==============================================================================
   Controles de formulário do design system
   ==============================================================================
   As peças que a tela de configurações e o modal de servidor montam: grupo de
   pílulas, paleta de amostras, slider de escala, interruptor, slider de volume e
   a matriz de permissões.

   Por que existem como componentes e não como markup repetido
   ==========================================================
   Cada um destes controles apareceu duas ou três vezes ao longo das telas com
   markup ligeiramente diferente — altura de 32 num lugar e 34 no outro, raio md
   num e lg no outro. É exatamente o sintoma que o usuário descreveu como "tudo
   com o mesmo peso visual e nenhuma hierarquia": quando cada tela reinventa o
   controle, nenhuma diferença de tamanho significa nada, porque todas são
   acidentais.

   Regra mantida em todos: `input` nativo por baixo sempre que existe um.
   Interruptor feito de `div` com `onClick` não recebe foco, não responde a barra
   de espaço, não é anunciado como interruptor e não aparece no envio do
   formulário. O que muda é a aparência, não o elemento.
   ============================================================================== */

import { useId, useState, type ReactNode } from 'react';

/* ==============================================================================
   Grupo de pílulas — escolha única entre 2 e 4 opções
   ==============================================================================
   Radiogroup, e não uma fileira de botões.

   A diferença é de teclado: num radiogroup as SETAS movem entre as opções e Tab
   sai do grupo. Com quatro botões, Tab pararia quatro vezes numa escolha única —
   e escolha única é justamente o caso em que as setas são o gesto certo.
============================================================================== */

export interface PillOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
  /** Segunda linha, opcional. Ex.: o nome do tema abaixo do rótulo. */
  hint?: string;
}

export function PillGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  size = 'md',
}: {
  /** Rótulo acessível do grupo. Não é renderizado — quem renderiza é o `SettingsRow`. */
  label: string;
  options: ReadonlyArray<PillOption<T>>;
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
}) {
  const height = size === 'sm' ? 28 : 34;

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex items-center"
      style={{
        gap: 2,
        padding: 3,
        borderRadius: 'var(--radius-full)',
        /* Poço: o grupo é um trilho escavado na superfície e a pílula ativa
           flutua dentro dele. Sem o poço, a pílula ativa parece o único botão e
           as outras parecem texto solto. */
        background: 'var(--color-surface-sunken)',
        boxShadow: 'inset 0 1px 2px oklch(0% 0 0 / 0.30)',
      }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className="flex items-center px-3 font-medium transition-all"
            style={{
              gap: 'var(--spacing-2)',
              height,
              borderRadius: 'var(--radius-full)',
              background: active ? 'var(--color-accent-solid)' : 'transparent',
              /*
               * Inativo em SECUNDÁRIO e não em terciário.
               *
               * Medido: terciário sobre `--color-surface-sunken` dá 4.31:1 no
               * tema claro — reprova AA. O poço é mais escuro que o card no
               * claro, então o mesmo token que passa sobre o card cai aqui. É a
               * mesma armadilha que o comentário de theme.css descreve para as
               * superfícies de interação, num sentido invertido.
               */
              color: active
                ? 'var(--color-on-solid)'
                : 'var(--color-text-secondary)',
              fontSize: 'var(--text-xs)',
              boxShadow: active
                ? 'inset 0 1px 0 oklch(100% 0 0 / 0.22), 0 2px 10px var(--color-accent-glow)'
                : 'none',
              transitionDuration: 'var(--duration-fast)',
            }}
          >
            {option.icon !== undefined && (
              <span aria-hidden="true" className="grid place-items-center">
                {option.icon}
              </span>
            )}
            <span className="whitespace-nowrap">{option.label}</span>
            {option.hint !== undefined && (
              <span style={{ fontSize: 'var(--text-2xs)', opacity: 0.75 }}>
                {option.hint}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ==============================================================================
   Paleta de amostras
   ==============================================================================
   Cinco quadrados arredondados, o escolhido com um CHECK dentro.

   O check não é adorno: com cinco cores vizinhas, "qual está selecionado" por
   anel de contorno obriga a comparar cinco contornos. O símbolo dentro do
   quadrado responde sem comparação — e funciona para quem não distingue os
   matizes entre si, que é justamente quem mais precisa da marca.

   As cores vêm de `--swatch-*` em theme.css, não de literais: cada amostra tem
   de ser o mesmo valor que o bloco `[data-accent]` correspondente aplica.
============================================================================== */

export interface AccentSwatch {
  value: string;
  label: string;
  /** Token CSS da cor. Ex.: `var(--swatch-violet)`. */
  color: string;
}

export const ACCENT_SWATCHES: ReadonlyArray<AccentSwatch> = [
  { value: 'violet', label: 'Violeta', color: 'var(--swatch-violet)' },
  { value: 'pink', label: 'Rosa', color: 'var(--swatch-pink)' },
  { value: 'green', label: 'Verde', color: 'var(--swatch-green)' },
  { value: 'blue', label: 'Azul', color: 'var(--swatch-blue)' },
  { value: 'amber', label: 'Âmbar', color: 'var(--swatch-amber)' },
];

export function SwatchPalette({
  label,
  swatches = ACCENT_SWATCHES,
  value,
  onChange,
}: {
  label: string;
  swatches?: ReadonlyArray<AccentSwatch>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex items-center"
         style={{ gap: 'var(--spacing-2)' }}>
      {swatches.map((swatch) => {
        const active = swatch.value === value;
        return (
          <button
            key={swatch.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(swatch.value)}
            title={swatch.label}
            className="grid place-items-center transition-transform"
            style={{
              width: 34,
              height: 34,
              borderRadius: 'var(--radius-md)',
              background: swatch.color,
              transform: active ? 'scale(1.06)' : 'none',
              /* O anel do selecionado usa a superfície como "vazio" entre a cor
                 e o anel de acento. Anel colado na cor lê como borda grossa. */
              boxShadow: active
                ? '0 0 0 2px var(--color-surface-overlay), 0 0 0 4px var(--color-accent), 0 2px 10px oklch(0% 0 0 / 0.40)'
                : 'inset 0 1px 0 oklch(100% 0 0 / 0.25), 0 2px 6px oklch(0% 0 0 / 0.35)',
              transitionDuration: 'var(--duration-fast)',
              transitionTimingFunction: 'var(--ease-spring)',
            }}
          >
            {active && (
              /* Traço escuro por cima da cor, e não branco: três das cinco
                 amostras são claras (âmbar, verde, rosa) e um check branco
                 desaparece nelas. Escuro passa nas cinco. */
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3.6 8.4l3 3 5.8-6.4" stroke="oklch(18% 0.03 292)"
                      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
            <span className="sr-only">{swatch.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ==============================================================================
   Slider de escala — rótulos nas PONTAS
   ==============================================================================
   "Pequena" e "Grande" nas extremidades em vez de um número no meio.

   O valor de `--font-scale` é 0.92, 1, 1.10 ou 1.22, e nenhum desses números
   significa nada para quem está escolhendo. O que a pessoa quer saber é para que
   lado arrastar. As pontas dizem isso e o efeito é visível na hora, porque a
   escala é aplicada na interface inteira.
============================================================================== */

export function ScaleSlider({
  label,
  minLabel,
  maxLabel,
  steps,
  value,
  onChange,
}: {
  label: string;
  minLabel: string;
  maxLabel: string;
  /** Valores discretos, do menor para o maior. */
  steps: ReadonlyArray<string>;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const index = Math.max(0, steps.indexOf(value));

  return (
    <div>
      <div className="flex items-center" style={{ gap: 'var(--spacing-3)' }}>
        <span className="shrink-0"
              style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}>
          {minLabel}
        </span>
        <input
          id={id}
          type="range"
          className="range-accent min-w-0 flex-1"
          aria-label={label}
          min={0}
          max={Math.max(0, steps.length - 1)}
          step={1}
          value={index}
          onChange={(event) => {
            const next = steps[Number(event.target.value)];
            if (next !== undefined) onChange(next);
          }}
        />
        {/* A ponta MAIOR é desenhada maior. Rótulo "Grande" no mesmo corpo de
            "Pequena" descreve a escala sem demonstrá-la. */}
        <span className="shrink-0"
              style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-secondary)' }}>
          {maxLabel}
        </span>
      </div>
    </div>
  );
}

/* ==============================================================================
   Interruptor
============================================================================== */

export function ToggleSwitch({
  label,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="relative shrink-0 transition-colors disabled:cursor-not-allowed disabled:opacity-45"
      style={{
        width: 42,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked
          ? 'var(--color-accent-solid)'
          : 'var(--color-surface-sunken)',
        boxShadow: checked
          ? 'inset 0 1px 0 oklch(100% 0 0 / 0.20), 0 0 12px var(--color-accent-glow)'
          : 'inset 0 1px 2px oklch(0% 0 0 / 0.35), 0 0 0 1px var(--color-border-default)',
        transitionDuration: 'var(--duration-fast)',
      }}
    >
      <span
        aria-hidden="true"
        className="absolute block transition-transform"
        style={{
          top: 3,
          left: 3,
          width: 18,
          height: 18,
          borderRadius: 'var(--radius-full)',
          background: checked ? 'var(--color-on-solid)' : 'var(--color-text-tertiary)',
          transform: checked ? 'translateX(18px)' : 'none',
          boxShadow: '0 1px 3px oklch(0% 0 0 / 0.45)',
          transitionDuration: 'var(--duration-fast)',
          transitionTimingFunction: 'var(--ease-out-quart)',
        }}
      />
    </button>
  );
}

/* ==============================================================================
   Volume por pessoa
   ==============================================================================
   O rótulo diz "só para você", e essa frase é o componente todo.

   Silenciar no SERVIDOR e baixar o volume LOCAL parecem a mesma ação e têm
   consequências opostas: a primeira cala a pessoa para todos na sala e fica no
   log de moderação, a segunda não sai deste navegador. Um controle chamado
   "volume" ao lado de um chamado "silenciar" não distingue os dois, e o erro que
   isso produz é um moderador calando alguém publicamente quando só queria
   diminuir o volume no fone.

   0% é apresentado como "mudo só para você" e não como "0%": um número zero num
   slider parece um valor entre outros, e este é um ESTADO.
============================================================================== */

export function VolumeSlider({
  name,
  volume,
  onChange,
}: {
  /** Nome da pessoa, para o rótulo acessível. */
  name: string;
  /** 0 a 3. Acima de 1 é amplificação com compressor. */
  volume: number;
  onChange: (volume: number) => void;
}) {
  /* ==========================================================================
     Até 300%
     ==========================================================================
     O teto era 100%, e ele não era escolha de interface: `element.volume` é
     definido em [0, 1] pela especificação, então não havia como oferecer mais.
     O relato — "o microfone está muito baixo" — batia num controle cujo máximo
     era "exatamente o que chegou", que era justamente o volume insuficiente.

     Quem amplifica agora é um `GainNode`, montado sob demanda em `VoiceAudio`.

     Acima de 100% o número fica em cor de aviso. Amplificar é útil e também é o
     que distorce: um controle que trata 180% como se fosse mais um valor normal
     não avisa que a distorção veio dele, e a pessoa conclui que o microfone da
     outra é que está ruim.
  ========================================================================== */
  const percent = Math.round(Math.min(3, Math.max(0, volume)) * 100);
  const muted = percent === 0;
  const amplificado = percent > 100;

  return (
    <div className="px-3 py-2">
      <div className="mb-1 flex items-baseline justify-between"
           style={{ gap: 'var(--spacing-2)' }}>
        <span className="font-medium"
              style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-secondary)' }}>
          Volume — só para você
        </span>
        <span className="tabular-nums"
              style={{
                fontSize: 'var(--text-2xs)',
                fontFamily: 'var(--font-mono)',
                color: muted
                  ? 'var(--color-danger-on-subtle)'
                  : amplificado
                    ? 'var(--color-warning)'
                    : 'var(--color-text-tertiary)',
              }}>
          {muted ? 'mudo' : `${percent}%`}
        </span>
      </div>

      <div className="flex items-center" style={{ gap: 'var(--spacing-2)' }}>
        {/* Botão de mudo local. É `volume = 0`, e o ícone diz que é o fone e não
            o microfone da outra pessoa. */}
        <button
          type="button"
          onClick={() => onChange(muted ? 1 : 0)}
          aria-label={
            muted
              ? `Ouvir ${name} de novo`
              : `Silenciar ${name} só para você`
          }
          title={
            muted
              ? `Ouvir ${name} de novo`
              : `Silenciar ${name} só para você`
          }
          className="grid shrink-0 place-items-center"
          style={{
            width: 24,
            height: 24,
            borderRadius: 'var(--radius-sm)',
            color: muted
              ? 'var(--color-danger-on-subtle)'
              : 'var(--color-text-tertiary)',
            background: muted ? 'var(--color-danger-subtle)' : 'transparent',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M7.6 3.4v9.2L4.6 10.2H2.8a.7.7 0 01-.7-.7V6.5a.7.7 0 01.7-.7h1.8l3-2.4z"
                  stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            {muted ? (
              <path d="M10.4 6.4l3.2 3.2M13.6 6.4l-3.2 3.2" stroke="currentColor"
                    strokeWidth="1.4" strokeLinecap="round" />
            ) : (
              <path d="M10.2 6.2a2.6 2.6 0 010 3.6M12.2 4.6a5 5 0 010 6.8"
                    stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            )}
          </svg>
        </button>

        <input
          type="range"
          className="range-accent range-neutral min-w-0 flex-1"
          aria-label={`Volume de ${name}, só para você`}
          min={0}
          max={300}
          step={5}
          value={percent}
          onChange={(event) => onChange(Number(event.target.value) / 100)}
        />
      </div>

      {/*
        A marca dos 100%.
        ----------------
        Num controle que vai a 200, "o volume original" deixa de ser o extremo
        direito e passa a ser um ponto no meio — invisível sem marcação. A
        pessoa que só quer voltar ao normal precisaria acertar 100 no olho, e
        acertaria 95 ou 105.

        `left: 33.33%` porque a faixa é 0–300: 100 é um TERÇO do percurso, não a
        metade. Um valor herdado de quando o máximo era 200 poria a marca em 150
        e apontaria para o lugar errado com toda a confiança de uma marca.
      */}
      <div className="relative" style={{ height: 12 }} aria-hidden="true">
        <span
          className="absolute"
          style={{
            left: '33.33%',
            top: 0,
            width: 1,
            height: 4,
            background: 'var(--color-border-strong)',
          }}
        />
        <span
          className="absolute -translate-x-1/2 tabular-nums"
          style={{
            left: '33.33%',
            top: 4,
            fontSize: 9,
            fontFamily: 'var(--font-mono)',
            color: 'var(--color-text-tertiary)',
          }}
        >
          100
        </span>
      </div>
    </div>
  );
}

/* ==============================================================================
   Matriz de permissões — cargos nas linhas, permissões nas colunas
   ==============================================================================
   Matriz e não uma tela por cargo.

   A pergunta real de quem configura permissões é comparativa: "quem pode apagar
   mensagem?". Numa tela por cargo isso exige abrir três cargos e memorizar; numa
   matriz é ler uma coluna. O custo é largura, resolvido com rolagem horizontal e
   a coluna de cargo GRUDADA na esquerda — sem ela, rolar para a quinta
   permissão perde a informação de qual linha é qual.
============================================================================== */

export interface MatrixRole {
  id: string;
  name: string;
  color?: string | null;
  /** Cargo que não pode ser editado (dono, @everyone travado). */
  locked?: boolean;
}

export interface MatrixPermission {
  id: string;
  label: string;
  description?: string;
}

export function PermissionMatrix({
  roles,
  permissions,
  granted,
  onToggle,
  readOnly = false,
}: {
  roles: ReadonlyArray<MatrixRole>;
  permissions: ReadonlyArray<MatrixPermission>;
  granted: (roleId: string, permissionId: string) => boolean;
  onToggle: (roleId: string, permissionId: string, next: boolean) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="overflow-x-auto"
         style={{ borderRadius: 'var(--radius-lg)', background: 'oklch(0% 0 0 / 0.14)' }}>
      <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead>
          <tr>
            <th
              scope="col"
              className="sticky left-0 z-10 px-3 py-2 text-left font-semibold uppercase"
              style={{
                /* A célula grudada precisa de fundo OPACO. Translúcida, o
                   conteúdo que passa por baixo dela na rolagem aparece através
                   do nome do cargo. */
                background: 'var(--color-surface-overlay)',
                fontSize: 'var(--text-2xs)',
                letterSpacing: 'var(--tracking-wide)',
                color: 'var(--color-text-tertiary)',
                boxShadow: 'inset 0 -1px 0 var(--color-border-subtle)',
              }}
            >
              Cargo
            </th>
            {permissions.map((permission) => (
              <th
                key={permission.id}
                scope="col"
                className="px-3 py-2 font-semibold"
                title={permission.description}
                style={{
                  fontSize: 'var(--text-2xs)',
                  color: 'var(--color-text-tertiary)',
                  whiteSpace: 'nowrap',
                  boxShadow: 'inset 0 -1px 0 var(--color-border-subtle)',
                }}
              >
                {permission.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {roles.map((role) => (
            <tr key={role.id}>
              <th
                scope="row"
                className="sticky left-0 z-10 px-3 py-2 text-left"
                style={{
                  background: 'var(--color-surface-overlay)',
                  boxShadow: 'inset 0 -1px 0 var(--color-border-subtle)',
                }}
              >
                <span className="flex items-center" style={{ gap: 'var(--spacing-2)' }}>
                  <span aria-hidden="true" className="block size-2 shrink-0 rounded-full"
                        style={{ background: role.color ?? 'var(--color-text-disabled)' }} />
                  <span className="truncate font-medium"
                        style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--color-text-primary)',
                        }}>
                    {role.name}
                  </span>
                </span>
              </th>
              {permissions.map((permission) => {
                const on = granted(role.id, permission.id);
                const locked = readOnly || role.locked === true;
                return (
                  <td key={permission.id} className="px-3 py-2 text-center"
                      style={{ boxShadow: 'inset 0 -1px 0 var(--color-border-subtle)' }}>
                    <span className="inline-flex">
                      <ToggleSwitch
                        label={`${permission.label} para ${role.name}`}
                        checked={on}
                        disabled={locked}
                        onChange={(next) => onToggle(role.id, permission.id, next)}
                      />
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ==============================================================================
   Linha administrável — ícone + nome + editar/apagar
   ==============================================================================
   As ações ficam SEMPRE visíveis e não só no hover.

   Revelar no hover é elegante e errado aqui: numa lista de canais a pessoa está
   procurando qual apagar, e ações invisíveis obrigam a passar o mouse por cada
   linha para descobrir se ela tem ações. O botão de apagar só ganha cor de
   perigo ao ser apontado — em repouso ele é cinza como o de editar, para uma
   lista de dez canais não parecer dez avisos vermelhos.
============================================================================== */

export function AdminRow({
  icon,
  title,
  subtitle,
  onEdit,
  onDelete,
  editLabel = 'Editar',
  deleteLabel = 'Apagar',
}: {
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  onEdit?: (() => void) | undefined;
  onDelete?: (() => void) | undefined;
  editLabel?: string;
  deleteLabel?: string;
}) {
  const [hover, setHover] = useState(false);
  const [dangerHover, setDangerHover] = useState(false);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="flex items-center px-3 py-2 transition-colors"
      style={{
        gap: 'var(--spacing-3)',
        borderRadius: 'var(--radius-md)',
        background: hover ? 'oklch(100% 0 0 / 0.05)' : 'transparent',
        transitionDuration: 'var(--duration-fast)',
      }}
    >
      {icon !== undefined && (
        <span aria-hidden="true" className="shrink-0"
              style={{ color: 'var(--color-text-tertiary)' }}>
          {icon}
        </span>
      )}

      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium"
              style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}>
          {title}
        </span>
        {subtitle !== undefined && (
          <span className="block truncate"
                style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}>
            {subtitle}
          </span>
        )}
      </span>

      <span className="flex shrink-0 items-center" style={{ gap: 2 }}>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            aria-label={`${editLabel} ${title}`}
            title={editLabel}
            className="grid place-items-center"
            style={{
              width: 28, height: 28,
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M11.2 2.6l2.2 2.2-8 8H3.2v-2.2l8-8z" stroke="currentColor"
                    strokeWidth="1.4" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            onMouseEnter={() => setDangerHover(true)}
            onMouseLeave={() => setDangerHover(false)}
            aria-label={`${deleteLabel} ${title}`}
            title={deleteLabel}
            className="grid place-items-center transition-colors"
            style={{
              width: 28, height: 28,
              borderRadius: 'var(--radius-sm)',
              color: dangerHover
                ? 'var(--color-danger-on-subtle)'
                : 'var(--color-text-tertiary)',
              background: dangerHover ? 'var(--color-danger-subtle)' : 'transparent',
              transitionDuration: 'var(--duration-fast)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3.4 5.2h9.2M6.4 5.2V3.8h3.2v1.4M4.8 5.2l.6 8h5.2l.6-8"
                    stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"
                    strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </span>
    </div>
  );
}
